import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getInvoicesByClientId, getClientLedger, addRevenuesLedgerEntry, deleteClient, updateRevenuesLedgerEntry, getClientById, deleteRevenuesLedgerEntry, updatePaymentEntry } from '../../firebase/database';
import { Client, LogisticsInvoice, ClientLedger } from '../../types';
import { generateClientLedgerPDF, generateClientLedgerExcel } from '../../services/ledgerPdfService';
import { openPDFInNewTab, generateRevenuesInvoicePDF } from '../../services/invoicePdfService';
import DateRangeModal from '../DateRangeModal';
import { useAuth } from '../../context/AuthContext';
import toast, { Toaster } from 'react-hot-toast';

const ClientsList: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'outstanding' | 'createdAt'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientInvoices, setClientInvoices] = useState<LogisticsInvoice[]>([]);
  const [showInvoices, setShowInvoices] = useState(false);
  const [generatingLedgerClientId, setGeneratingLedgerClientId] = useState<string | null>(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerLogoPath, setLedgerLogoPath] = useState<string | null>(null);
  const [majorityShippingMethod, setMajorityShippingMethod] = useState<'air' | 'sea'>('sea');
  const [paymentCredit, setPaymentCredit] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [paymentDescription, setPaymentDescription] = useState<string>('');
  const { currentUser } = useAuth();
  const [currentLedger, setCurrentLedger] = useState<ClientLedger | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editCredit, setEditCredit] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [refreshingLedger, setRefreshingLedger] = useState<boolean>(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [availableLogisticsCompanies, setAvailableLogisticsCompanies] = useState<string[]>([]);
  const [pendingActionType, setPendingActionType] = useState<'pdf' | 'excel' | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateRange, setDateRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [pendingPdfContext, setPendingPdfContext] = useState<{ ledger: ClientLedger; invoices: LogisticsInvoice[] } | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  // Detect sidebar width dynamically
  useEffect(() => {
    const updateSidebarWidth = () => {
      const sidebar = document.querySelector('[class*="fixed inset-y-0 left-0"]') as HTMLElement;
      if (sidebar) {
        const rect = sidebar.getBoundingClientRect();
        const isVisible = window.getComputedStyle(sidebar).transform === 'none' || 
                         window.getComputedStyle(sidebar).transform === 'matrix(1, 0, 0, 1, 0, 0)';
        setSidebarWidth(isVisible ? rect.width : 0);
      } else {
        setSidebarWidth(0);
      }
    };

    updateSidebarWidth();
    window.addEventListener('resize', updateSidebarWidth);
    
    // Use MutationObserver to detect sidebar changes
    const observer = new MutationObserver(updateSidebarWidth);
    const sidebar = document.querySelector('[class*="fixed inset-y-0 left-0"]');
    if (sidebar) {
      observer.observe(sidebar, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    // Also observe for clicks that might toggle sidebar
    const checkInterval = setInterval(updateSidebarWidth, 500);

    return () => {
      window.removeEventListener('resize', updateSidebarWidth);
      observer.disconnect();
      clearInterval(checkInterval);
    };
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clientsData = await getClients();
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeLedgerModal = async () => {
    setShowLedgerModal(false);
    setCurrentLedger(null);
    setSelectedClient(null);
    // Refresh the client list to show updated outstanding balance
    await loadClients();
  };


  const loadClientInvoices = async (clientId: string) => {
    try {
      const invoices = await getInvoicesByClientId(clientId);
      setClientInvoices(invoices);
    } catch (error) {
      console.error('Error loading client invoices:', error);
    }
  };

  const handleViewInvoices = async (client: Client) => {
    setSelectedClient(client);
    await loadClientInvoices(client.id);
    setShowInvoices(true);
  };

  const handleViewLedger = async (client: Client) => {
    try {
      setSelectedClient(client);
      setPaymentCredit('');
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentDescription('');
      setGeneratingLedgerClientId(client.id);
      const [ledger, invoices] = await Promise.all([
        getClientLedger(client.id),
        getInvoicesByClientId(client.id)
      ]);
      setCurrentLedger(ledger);
      setClientInvoices(invoices); // Store invoices for reference in the table
      // Determine logistics company logo similar to PDF logic
      const firstInvoice = invoices.length > 0 ? invoices[0] : null;
      const isVector = firstInvoice?.logisticsCompany === 'VECTOR LOGISTICS';
      setLedgerLogoPath(isVector ? '/assets/vector_logo.png' : '/assets/barq_logo-min.png');
      // Determine majority shipping method for header labels
      const airCount = invoices.filter((inv) => inv.shippingMethod === 'air').length;
      const seaCount = invoices.filter((inv) => inv.shippingMethod === 'sea').length;
      setMajorityShippingMethod(airCount > seaCount ? 'air' : 'sea');
      setShowLedgerModal(true);
    } catch (e) {
      toast.error('Failed to load ledger');
    } finally {
      setGeneratingLedgerClientId(null);
    }
  };

  // Helper function to check if outstanding/remarks should be hidden for this client
  const shouldHideOutstandingAndRemarks = (client: Client | null): boolean => {
    return client?.clientType === 'import';
  };

  // Helper function to check if an entry is from an import invoice
  const isImportInvoice = (entry: any): boolean => {
    if (entry.type !== 'invoice' || !entry.invoiceId) return false;
    const invoice = clientInvoices.find(inv => inv.id === entry.invoiceId);
    return invoice?.invoiceType === 'import';
  };

  const savePayment = async () => {
    if (!selectedClient) return;
    const credit = Number(paymentCredit);
    if (isNaN(credit) || credit <= 0) {
      toast.error('Enter a valid credit amount');
      return;
    }
    try {
      setGeneratingLedgerClientId(selectedClient.id);
      // Balance will be recalculated by addRevenuesLedgerEntry based on date order
      await addRevenuesLedgerEntry(selectedClient.id, {
        date: new Date(paymentDate),
        description: paymentDescription.trim() || 'Payment',
        debit: 0,
        credit,
        balance: 0, // Will be recalculated by the database function
        type: 'payment',
        createdBy: currentUser?.id || 'system'
      } as any);
      
      // Add a small delay to ensure the database update is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const ledger = await getClientLedger(selectedClient.id);
      setCurrentLedger(ledger);
      setPaymentCredit('');
      
      // Also refresh the client list to update outstanding balance
      await loadClients();
      
      toast.success('Payment saved');
    } catch (e) {
      console.error('Error saving payment', e);
      toast.error('Failed to save payment');
    } finally {
      setGeneratingLedgerClientId(null);
    }
  };

  const startEdit = (entry: any) => {
    setEditingEntry(entry.id);
    setEditCredit(entry.credit.toString());
    setEditDescription(entry.description || '');
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setEditCredit('');
    setEditDescription('');
  };

  const saveEdit = async () => {
    if (!editingEntry || !editCredit) {
      toast.error('Please fill in the credit amount');
      return;
    }

    const credit = Number(editCredit);
    if (isNaN(credit) || credit <= 0) {
      toast.error('Enter a valid credit amount');
      return;
    }

    try {
      setRefreshingLedger(true);
      await updatePaymentEntry(currentLedger!.clientId, editingEntry, credit, editDescription.trim() || 'Payment');
      
      // Add a small delay to ensure the database update is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const updatedLedger = await getClientLedger(currentLedger!.clientId);
      setCurrentLedger(updatedLedger);
      
      // Also refresh the client list to update outstanding balance
      await loadClients();
      
      cancelEdit();
      toast.success('Payment updated successfully');
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Failed to update payment');
    } finally {
      setRefreshingLedger(false);
    }
  };

  const computeDateBounds = (ledger: ClientLedger) => {
    if (!ledger.entries || ledger.entries.length === 0) {
      const today = new Date();
      const d = today.toISOString().slice(0, 10);
      return { min: d, max: d };
    }
    const dates = ledger.entries.map((e: any) => new Date(e.date));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    return { min: minDate.toISOString().slice(0, 10), max: maxDate.toISOString().slice(0, 10) };
  };

  const viewLedgerPdf = async () => {
    if (!selectedClient) return;
    try {
      setGeneratingLedgerClientId(selectedClient.id);
      const [clientLedger, invoices] = await Promise.all([
        getClientLedger(selectedClient.id),
        getInvoicesByClientId(selectedClient.id)
      ]);
      // Open date range modal like manual ledger
      const bounds = computeDateBounds(clientLedger);
      setDateRange(bounds);
      setPendingPdfContext({ ledger: clientLedger, invoices });
      setShowDateModal(true);
    } catch (error) {
      console.error('Error generating ledger:', error);
      toast.error('Error generating ledger. Please try again.');
    } finally {
      setGeneratingLedgerClientId(null);
    }
  };

  const onConfirmClientDateRange = async (fromDate: string, toDate: string) => {
    if (!selectedClient || !pendingPdfContext) return;
    const { ledger, invoices } = pendingPdfContext;

    // Build filtered ledger with opening balance from entries before fromDate
    const from = fromDate ? new Date(fromDate + 'T00:00:00') : new Date(0);
    const to = toDate ? new Date(toDate + 'T23:59:59') : new Date(9999, 11, 31);

    const entriesSorted = [...ledger.entries].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const priorEntries = entriesSorted.filter((e: any) => new Date(e.date) < from);
    const openingBalanceAmount = priorEntries.length > 0 ? Math.round((priorEntries[priorEntries.length - 1] as any).balance || 0) : 0;

    const inRangeEntries = entriesSorted.filter((e: any) => {
      const d = new Date(e.date);
      return d >= from && d <= to;
    });

    const openingBalanceEntry: any = {
      id: 'opening-balance',
      type: 'adjustment',
      description: 'Opening Balance',
      date: from,
      debit: 0,
      credit: 0,
      balance: openingBalanceAmount
    };

    const filteredLedger: ClientLedger = {
      ...ledger,
      entries: [openingBalanceEntry, ...inRangeEntries]
    } as any;

    try {
      // Handle multiple logistics companies selection before PDF
      const logisticsCompanies = Array.from(new Set(
        invoices
          .map(inv => inv.logisticsCompany)
          .filter(company => company && company.trim() !== '')
      ));

      if (logisticsCompanies.length > 1) {
        setAvailableLogisticsCompanies(logisticsCompanies);
        setPendingActionType('pdf');
        setShowLogisticsModal(true);
      } else {
        const ledgerPDF = await generateClientLedgerPDF(filteredLedger, invoices, undefined, selectedClient.clientType);
        openPDFInNewTab(ledgerPDF, 'Ledger PDF');
      }
    } catch (error) {
      toast.error('Error generating ledger PDF');
    } finally {
      setShowDateModal(false);
      setPendingPdfContext(null);
    }
  };

  const viewLedgerExcel = async () => {
    if (!selectedClient) return;
    try {
      setGeneratingLedgerClientId(selectedClient.id);
      const [clientLedger, invoices] = await Promise.all([
        getClientLedger(selectedClient.id),
        getInvoicesByClientId(selectedClient.id)
      ]);
      
      // Check for multiple different logistics companies
      const logisticsCompanies = Array.from(new Set(
        invoices
          .map(invoice => invoice.logisticsCompany)
          .filter(company => company && company.trim() !== '')
      ));
      
      if (logisticsCompanies.length > 1) {
        // Show modal to select logistics company
        setAvailableLogisticsCompanies(logisticsCompanies);
        setPendingActionType('excel');
        setShowLogisticsModal(true);
        setGeneratingLedgerClientId(null);
        return;
      }
      
      generateClientLedgerExcel(clientLedger, invoices, undefined, selectedClient.clientType);
    } catch (error) {
      console.error('Error generating Excel:', error);
      toast.error('Error generating Excel. Please try again.');
    } finally {
      setGeneratingLedgerClientId(null);
    }
  };

  const closeInvoicesModal = () => {
    setShowInvoices(false);
    setSelectedClient(null);
    setClientInvoices([]);
  };

  const handleDeleteClient = async (client: Client) => {
    setClientToDelete(client);
    await loadClientInvoices(client.id);
    setShowDeleteDialog(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    
    try {
      setDeletingClientId(clientToDelete.id);
      await deleteClient(clientToDelete.id);
      await loadClients(); // Reload the clients list
      setShowDeleteDialog(false);
      setClientToDelete(null);
      toast.success('Client and all related invoices have been deleted successfully.');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client. Please try again.');
    } finally {
      setDeletingClientId(null);
    }
  };

  const navigateToInvoices = () => {
    if (!clientToDelete) return;
    setShowDeleteDialog(false);
    navigate('/revenues/all-invoices', { 
      state: { searchTerm: clientToDelete.name } 
    });
  };

  // Filter and sort clients
  const filteredClients = clients
    .filter(client =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.uniqueId.includes(searchQuery) ||
      client.clientType.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'outstanding':
          comparison = a.outstanding - b.outstanding;
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const handleSort = (field: 'name' | 'outstanding' | 'createdAt') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'outstanding' | 'createdAt') => {
    if (sortBy !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }

    return sortOrder === 'asc' ? (
      <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    );
  };

  const totalOutstanding = clients.reduce((sum, client) => sum + client.outstanding, 0);

  if (loading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        {/* Background Image with Blur */}
        <div 
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/background.png)',
            filter: 'blur(0px)',
            transform: 'scale(1.1)'
          }}
        />
        <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
        
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white drop-shadow-lg">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-8">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          filter: 'blur(0px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />
      
      {/* Content */}
      <div className="relative z-10"  >
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white drop-shadow-lg">Client Ledgers</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm font-medium text-gray-800 bg-white/70 backdrop-blur-md border border-white/30 rounded-lg hover:bg-white/70 transition-all duration-200"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/70 backdrop-blur-lg overflow-hidden shadow-xl rounded-2xl border-2 border-white/20">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-gray-800 text-lg">👥</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-gray-800/80 truncate drop-shadow-md">Total Clients</dt>
                    <dd className="text-lg font-bold text-gray-800 drop-shadow-lg">{clients.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-lg overflow-hidden shadow-xl rounded-2xl border-2 border-white/20">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                    <span className="text-gray-800 text-lg">💰</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-gray-800/80 truncate drop-shadow-md">Total Outstanding</dt>
                    <dd className="text-lg font-bold text-gray-800 drop-shadow-lg">PKR {Math.round(totalOutstanding).toLocaleString()}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-lg overflow-hidden shadow-xl rounded-2xl border-2 border-white/20">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <span className="text-gray-800 text-lg">📊</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-semibold text-gray-800/80 truncate drop-shadow-md">Average Outstanding</dt>
                    <dd className="text-lg font-bold text-gray-800 drop-shadow-lg">
                      PKR {Math.round(totalOutstanding / clients.length).toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/70 backdrop-blur-lg shadow-xl rounded-2xl border-2 border-white/20 mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex-1 max-w-lg">
                <label htmlFor="search" className="sr-only">Search clients</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-800/70" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 bg-white/70 backdrop-blur-sm border-2 border-white/30 rounded-lg leading-5 placeholder-gray-500 text-gray-900 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                    placeholder="Search clients by name, ID, or type..."
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm font-semibold text-gray-800 drop-shadow-md">Sort by:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-') as ['name' | 'outstanding' | 'createdAt', 'asc' | 'desc'];
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block w-full pl-3 pr-10 py-2.5 text-gray-900 bg-white/70 backdrop-blur-sm border-2 border-white/30 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 sm:text-sm rounded-lg"
                >
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="outstanding-asc">Outstanding (Low to High)</option>
                  <option value="outstanding-desc">Outstanding (High to Low)</option>
                  <option value="createdAt-asc">Date Added (Oldest First)</option>
                  <option value="createdAt-desc">Date Added (Newest First)</option>
                </select>
              </div>
            </div>
          </div>

        </div>

        {/* Clients Table */}
        <div className="bg-white/70 backdrop-blur-lg shadow-xl rounded-2xl border-2 border-white/20">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 drop-shadow-lg">
                Client Ledgers ({filteredClients.length} of {clients.length})
              </h3>
            </div>

            {filteredClients.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🔍</div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No clients found</h4>
                <p className="text-gray-500">Try adjusting your search criteria.</p>
              </div>
            ) : (
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('name')}
                          className="group inline-flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Client Name</span>
                          {getSortIcon('name')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Info
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('outstanding')}
                          className="group inline-flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Outstanding</span>
                          {getSortIcon('outstanding')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('createdAt')}
                          className="group inline-flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Date Added</span>
                          {getSortIcon('createdAt')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div>
                            <div className="text-xs font-medium text-gray-900">{client.name}</div>
                            <div className="text-xs text-gray-500">ID: {client.id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div>
                            <div className="text-xs text-gray-900">Unique ID: {client.uniqueId}</div>
                            <div className="text-xs text-gray-500">Type: {client.clientType}</div>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="text-xs text-gray-900 max-w-xs truncate">
                            {client.address}
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${client.outstanding > 10000
                              ? 'bg-red-100 text-red-800'
                              : client.outstanding > 5000
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                            PKR {Math.round(client.outstanding).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {new Date(client.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs font-medium">
                          <div className="inline-flex rounded-md shadow-sm overflow-hidden border border-gray-200">
                            <button
                              onClick={() => handleViewInvoices(client)}
                              className="px-2 py-1 text-blue-600 hover:bg-blue-50 flex items-center gap-1"
                            >
                              <span>📄</span>
                              <span className="text-xs">Invoices</span>
                            </button>
                            <div className="w-px bg-gray-200" />
                            <button
                              onClick={() => handleViewLedger(client)}
                              disabled={generatingLedgerClientId === client.id}
                              className={`px-2 py-1 text-indigo-600 hover:bg-indigo-50 flex items-center gap-1 ${generatingLedgerClientId === client.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span>📘</span>
                              <span className="text-xs">{generatingLedgerClientId === client.id ? 'Generating…' : 'Ledger'}</span>
                            </button>
                            <div className="w-px bg-gray-200" />
                            <button
                              onClick={() => handleDeleteClient(client)}
                              disabled={deletingClientId === client.id}
                              className={`px-2 py-1 text-red-600 hover:bg-red-50 flex items-center gap-1 ${deletingClientId === client.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span>🗑️</span>
                              <span className="text-xs">{deletingClientId === client.id ? 'Deleting…' : 'Delete'}</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoices Modal */}
      {showInvoices && selectedClient && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 transition-all duration-300"
          style={{ left: `${sidebarWidth}px` }}
        >
          <div className="h-full overflow-y-auto flex items-center justify-center p-4">
            <div className="relative w-full max-w-4xl mx-auto p-5 border shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Invoices for {selectedClient.name}
              </h3>
              <button
                onClick={closeInvoicesModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {clientInvoices.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📄</div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h4>
                <p className="text-gray-500">This client doesn't have any invoices yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {invoice.date.toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {invoice.invoiceType.charAt(0).toUpperCase() + invoice.invoiceType.slice(1)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {invoice.shippingMethod.charAt(0).toUpperCase() + invoice.shippingMethod.slice(1)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          PKR {Math.round(invoice.total).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : invoice.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : invoice.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <div className="flex space-x-2">
                            {currentUser?.role === 'authorizer' && (
                              <>
                                <button
                                  className="text-indigo-600 hover:text-indigo-900"
                                  onClick={() => navigate(`/edit-invoice/${invoice.id}`)}
                                >
                                  Edit
                                </button>
                                <span className="text-gray-400">|</span>
                              </>
                            )}
                            <button
                              className="text-green-600 hover:text-green-900"
                              onClick={async () => {
                                try {
                                  // Get client address
                                  const client = await getClientById(invoice.clientId);
                                  const clientAddress = client?.address;
                                  
                                  const pdf = await generateRevenuesInvoicePDF(invoice, clientAddress);
                                  openPDFInNewTab(pdf, 'Invoice PDF');
                                } catch (e) {
                                  toast.error('Failed to open invoice PDF');
                                }
                              }}
                            >
                              View PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeInvoicesModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      {showLedgerModal && selectedClient && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[60] transition-all duration-300"
          style={{ left: `${sidebarWidth}px` }}
        >
          <div className="h-full overflow-y-auto flex items-center justify-center p-4">
            <div className="relative w-full max-w-[1400px] mx-auto p-5 border shadow-lg rounded-md bg-white my-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {ledgerLogoPath && (
                  <img src={ledgerLogoPath} alt="Logo" className="h-6 w-auto" />
                )}
                <h3 className="text-lg font-medium text-gray-900">Ledger Actions - {selectedClient.name}</h3>
              </div>
              <button onClick={closeLedgerModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Opening Balance Display */}
            {(selectedClient as any).openingBalance && (selectedClient as any).openingBalance > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Opening Balance</h4>
                    <p className="text-xs text-blue-600">Starting balance for this client</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-900">
                      PKR {Math.round((selectedClient as any).openingBalance).toLocaleString()}
                    </div>
                    <div className="text-xs text-blue-600">Initial outstanding amount</div>
                  </div>
                </div>
              </div>
            )}

            {/* Full ledger table with editable last row for payment */}
            <div className="relative overflow-x-auto overflow-y-auto max-h-[500px] border rounded-md">
              {refreshingLedger && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-600">Updating ledger...</span>
                  </div>
                </div>
              )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{majorityShippingMethod === 'sea' ? 'BL No.' : 'AWB'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{majorityShippingMethod === 'sea' ? 'Container' : 'Package'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{majorityShippingMethod === 'sea' ? 'Port of Loading' : 'Airport'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider bg-yellow-50 text-yellow-800">Balance</th>
                    {!shouldHideOutstandingAndRemarks(selectedClient) && (
                      <>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Days Outstand</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentLedger?.entries.map((entry, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {entry.type === 'adjustment' && entry.description === 'Opening Balance' 
                          ? 'Opening Balance' 
                          : entry.date.toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">{entry.type === 'invoice' ? (entry as any).invoiceNumber || '' : ''}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {entry.type === 'invoice' ? (entry.shippingMethod === 'air' ? entry.awbNumber : entry.blNumber) : ''}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {entry.type === 'invoice' ? (
                          (() => {
                            const val: any = entry.shippingMethod === 'sea' ? (entry as any).containerNumber : (entry as any).packageCount;
                            if (Array.isArray(val)) {
                              return (
                                <div className="space-y-0.5">
                                  {val.filter((v: string) => v && v.trim() !== '').map((v: string, i: number) => (
                                    <div key={i}>{v}</div>
                                  ))}
                                </div>
                              );
                            }
                            return val || '';
                          })()
                        ) : ''}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {entry.type === 'invoice' ? (entry.shippingMethod === 'air' ? entry.airportOfDestination : entry.portOfLoading) : ''}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        {editingEntry === entry.id ? (
                          <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full px-2 py-1 border rounded"
                            placeholder="Payment description..."
                          />
                        ) : (
                          entry.description || ''
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700">{entry.debit ? Math.round(entry.debit).toLocaleString() : ''}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {editingEntry === entry.id ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editCredit}
                            onChange={(e) => setEditCredit(e.target.value)}
                            className="w-20 px-2 py-1 border rounded text-right"
                          />
                        ) : (
                          entry.credit ? Math.round(entry.credit).toLocaleString() : ''
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right bg-yellow-50 text-yellow-800 font-semibold">{Math.round(entry.balance).toLocaleString()}</td>
                      {!shouldHideOutstandingAndRemarks(selectedClient) && (
                        <>
                          <td className="px-3 py-2 text-sm text-center text-gray-700">
                            {selectedClient?.clientType === 'both' && isImportInvoice(entry) ? '—' : (
                              entry.type === 'invoice' && entry.remarks !== 'adjusted' ? (() => {
                                const today = new Date();
                                const start = new Date(entry.date);
                                const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                                return diff > 0 ? `+${diff}` : '';
                              })() : ''
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            {selectedClient?.clientType === 'both' && isImportInvoice(entry) ? '—' : (
                              entry.type === 'invoice' ? (
                                <select
                                  value={(entry as any).remarks || 'outstand'}
                                  onChange={async (e) => {
                                    try {
                                      await updateRevenuesLedgerEntry(currentLedger!.clientId, entry.id, { remarks: e.target.value as any });
                                      const ledger = await getClientLedger(currentLedger!.clientId);
                                      setCurrentLedger(ledger);
                                    } catch (err) {
                                      toast.error('Failed to update remarks');
                                    }
                                  }}
                                  className={`px-2 py-1 border rounded ${((entry as any).remarks || 'outstand') === 'outstand' ? 'bg-red-100 text-red-800' : ((entry as any).remarks || 'outstand') === 'adjusted' ? 'bg-green-100 text-green-800' : ''}`}
                                >
                                  <option value="outstand">Outstand</option>
                                  <option value="adjusted">Adjusted</option>
                                </select>
                              ) : ''
                            )}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-sm text-center">
                        {entry.type === 'payment' && (
                          <div className="flex items-center gap-2">
                            {editingEntry === entry.id ? (
                              <>
                                <button
                                  onClick={saveEdit}
                                  className="px-2 py-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                                  title="Save changes"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                  title="Cancel edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEdit(entry)}
                                  className="px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                  title="Edit payment"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to delete this payment?')) {
                                      try {
                                        await deleteRevenuesLedgerEntry(currentLedger!.clientId, entry.id);
                                        
                                        // Add a small delay to ensure the database update is complete
                                        await new Promise(resolve => setTimeout(resolve, 100));
                                        
                                        const ledger = await getClientLedger(currentLedger!.clientId);
                                        setCurrentLedger(ledger);
                                        
                                        // Also refresh the client list to update outstanding balance
                                        await loadClients();
                                      } catch (err) {
                                        toast.error('Failed to delete payment');
                                      }
                                    }
                                  }}
                                  className="px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                  title="Delete payment"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Editable payment row */}
                  <tr>
                    <td className="px-3 py-2">
                      <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="w-full px-2 py-1 border rounded" />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-400">—</td>
                    <td className="px-3 py-2 text-sm text-gray-400">—</td>
                    <td className="px-3 py-2 text-sm text-gray-400">—</td>
                    <td className="px-3 py-2 text-sm text-gray-400">—</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={paymentDescription}
                        onChange={(e) => setPaymentDescription(e.target.value)}
                        className="w-full px-2 py-1 border rounded"
                        placeholder="Payment description..."
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-right text-gray-400">—</td>
                    <td className="px-3 py-2">
                      <input type="number" min="0" step="0.01" value={paymentCredit} onChange={(e) => setPaymentCredit(e.target.value)} className="w-full px-2 py-1 border rounded text-right" placeholder="0.00" />
                    </td>
                    <td className="px-3 py-2 text-sm text-right text-gray-700">
                      {(() => {
                        const credit = Number(paymentCredit) || 0;
                        if (!currentLedger?.entries || currentLedger.entries.length === 0) {
                          return Math.max(0, Math.round(-credit)).toLocaleString();
                        }
                        
                        // Sort entries by date to find the balance before the payment date
                        const sortedEntries = [...currentLedger.entries].sort((a, b) => {
                          // Opening balance always comes first
                          if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
                          if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
                          return a.date.getTime() - b.date.getTime();
                        });
                        
                        const paymentDateObj = new Date(paymentDate);
                        // Find the last entry on or before the payment date
                        let balanceBeforePayment = 0;
                        for (const entry of sortedEntries) {
                          const entryDate = new Date(entry.date);
                          if (entryDate <= paymentDateObj) {
                            balanceBeforePayment = entry.balance || 0;
                          } else {
                            break;
                          }
                        }
                        
                        // Calculate new balance after payment
                        const newBalance = Math.max(0, Math.round(balanceBeforePayment - credit));
                        return newBalance.toLocaleString();
                      })()}
                    </td>
                    {!shouldHideOutstandingAndRemarks(selectedClient) && (
                      <>
                        <td className="px-3 py-2 text-sm text-center text-gray-400">—</td>
                        <td className="px-3 py-2 text-sm text-gray-400">—</td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={closeLedgerModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button onClick={viewLedgerPdf} className="px-4 py-2 text-sm font-medium text-gray-100 bg-indigo-500 hover:bg-indigo-600 rounded-md">View Ledger PDF</button>
              <button onClick={viewLedgerExcel} className="px-4 py-2 text-sm font-medium text-gray-100 bg-green-500 hover:bg-green-600 rounded-md">View Excel</button>
              <button
                onClick={savePayment}
                disabled={generatingLedgerClientId === selectedClient.id}
                className={`px-4 py-2 text-sm font-medium text-gray-100 bg-green-600 hover:bg-green-700 rounded-md ${generatingLedgerClientId === selectedClient.id ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {generatingLedgerClientId === selectedClient.id ? 'Saving…' : 'Save'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Warning Dialog */}
      {showDeleteDialog && clientToDelete && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 transition-all duration-300"
          style={{ left: `${sidebarWidth}px` }}
        >
          <div className="h-full overflow-y-auto flex items-center justify-center p-4">
            <div className="relative w-full max-w-md mx-auto p-5 border shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Delete Client</h3>
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">Warning: Client has invoices</h4>
                  <p className="text-sm text-gray-500">
                    This client has {clientInvoices.length} invoice(s). Deleting will remove the client and all related data.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Client: {clientToDelete.name}</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This action will permanently delete:</p>
                      <ul className="list-disc list-inside mt-1">
                        <li>The client record</li>
                        <li>All {clientInvoices.length} invoice(s) associated with this client</li>
                        <li>All ledger entries and payment records</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={navigateToInvoices}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
              >
                View Invoices
              </button>
              <button
                onClick={confirmDeleteClient}
                disabled={deletingClientId === clientToDelete.id}
                className={`px-4 py-2 text-sm font-medium text-gray-800 bg-red-600 hover:bg-red-700 rounded-md transition-colors ${deletingClientId === clientToDelete.id ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {deletingClientId === clientToDelete.id ? 'Deleting...' : 'Delete Client & Invoices'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Logistics Company Selection Modal */}
      {showLogisticsModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 transition-all duration-300"
          style={{ left: `${sidebarWidth}px` }}
        >
          <div className="h-full overflow-y-auto flex items-center justify-center p-4">
            <div className="relative w-full max-w-md mx-auto p-5 border shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Select Logistics Company for {pendingActionType === 'pdf' ? 'PDF' : 'Excel'}
              </h3>
              <button
                onClick={() => setShowLogisticsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">Multiple Logistics Companies Detected</h4>
                  <p className="text-sm text-gray-500">
                    This ledger contains invoices from multiple logistics companies. Please select which company logo to display.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {availableLogisticsCompanies.map((company) => (
                  <button
                    key={company}
                    onClick={async () => {
                      setShowLogisticsModal(false);
                      if (pendingActionType) {
                        // We need to get the invoices again to pass the selected company
                        const [clientLedger, invoices] = await Promise.all([
                          getClientLedger(selectedClient!.id),
                          getInvoicesByClientId(selectedClient!.id)
                        ]);
                        
                        if (pendingActionType === 'pdf') {
                          const ledgerPDF = await generateClientLedgerPDF(clientLedger, invoices, company, selectedClient!.clientType);
                          openPDFInNewTab(ledgerPDF, 'Ledger PDF');
                        } else if (pendingActionType === 'excel') {
                          generateClientLedgerExcel(clientLedger, invoices, company, selectedClient!.clientType);
                        }
                      }
                    }}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-orange-600 text-sm font-bold">
                            {company === 'VECTOR LOGISTICS' ? 'V' : 'B'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {company === 'VECTOR LOGISTICS' ? 'VECTOR LOGISTICS' : 'BARQ CARGO SYSTEM PVT LTD.'}
                          </div>
                          <div className="text-xs text-gray-500">Use this company's logo</div>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowLogisticsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      </div>
      {/* Date Range Modal */}
      <DateRangeModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        onConfirm={onConfirmClientDateRange}
        minDate={dateRange.min}
        maxDate={dateRange.max}
        title="Select Date Range for PDF"
      />
    </div>
  );
};

export default ClientsList;

export {};

// Date Range Modal for Client Ledger PDF
/* Rendered at root level of this component */

